import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { ui } from '@procuros/datachecks';

import { useNavigationContext } from 'contexts/SchemableFormNavigationContext/SchemableFormNavigationContext';
import { getGoogleDriveDownloadUrl } from 'services/http/http';
import { DisplayModes } from 'services/webedi/types';
import { isWebEDIFormError, WebEDIAnnotation } from 'services/webediErrors/types';
import { Checkbox } from 'components/Form/Checkbox/Checkbox';
import { FieldsetLegend } from 'components/Form/Input/FieldsetLegend/FieldsetLegend';
import { ExternalFilesList } from 'components/SchemableForm/fields/components/ExternalFilesList/ExternalFilesList';
import { Translate } from 'components/Translate/Translate';

type ExternalActionFieldProps = {
  field: ui.ExternalActionField;
  fieldId: string;
  isReadOnly: boolean;
  mode: DisplayModes;
  variant?: 'large';
};

export const ExternalActionField = ({ field, fieldId, isReadOnly, mode, variant }: ExternalActionFieldProps) => {
  const { t } = useTranslation();
  const value = useWatch({ name: fieldId });
  const { register } = useFormContext();
  const { service } = useNavigationContext<WebEDIAnnotation>();
  const item = service.useItemByFieldPath(fieldId);

  const isViewMode = mode === DisplayModes.view;
  const isLargeVariant = variant === 'large';

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3">
        {/* In view mode, render a compact label (no description, default size) to match the FileRow style */}
        <FieldsetLegend
          label={field.label}
          description={isViewMode || !field.description ? undefined : <Translate i18nKey={field.description} />}
          variant={!isViewMode && isLargeVariant ? 'large' : 'default'}
          required={!isViewMode && field.modality === 'MANDATORY'}
        />

        {field.type_config.external_files?.length ? (
          <ExternalFilesList
            externalFiles={field.type_config.external_files}
            action={{
              label: t('common:form.externalAction.downloadFile'),
              onClick: (file) => {
                const downloadUrl = getGoogleDriveDownloadUrl(file.url);
                window.open(downloadUrl ?? file.url, '_blank');
              },
              RightIcon: ArrowTopRightOnSquareIcon,
            }}
          />
        ) : null}
      </div>

      <div className="flex flex-col gap-3">
        {isViewMode ? null : (
          <p className="text-sm text-gray-700">{t('common:form.externalAction.confirmationPrompt')}</p>
        )}
        <div className="rounded-md border border-gray-200 p-4">
          <Checkbox
            label={t('common:form.externalAction.confirmationLabel')}
            data-field={fieldId}
            {...register(fieldId)}
            checked={Boolean(value)}
            disabled={isViewMode || isReadOnly}
            error={Boolean(item)}
          />
        </div>
        {isLargeVariant && isWebEDIFormError(item) && <span className="text-sm text-red-600">{item.description}</span>}
      </div>
    </div>
  );
};
